import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Avatar,
  Tag,
  Dropdown,
  Space,
  message,
  Tabs,
  Modal,
  Form,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  TeamOutlined,
  SafetyCertificateOutlined,
  MailOutlined,
  SearchOutlined,
  PlusOutlined,
  EllipsisOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { vnDayjs } from '../utils/vnDateTime';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  canUpdatePermissions,
  canDeactivateAdmin,
} from '../utils/adminPermissions';
import {
  FULL_PERMISSIONS,
  DEFAULT_ADMIN_PERMISSIONS,
  DEFAULT_SUPPORT_PERMISSIONS,
  type PermissionsMap,
  type PermissionLevel,
} from '../constants/adminPermissions';
import { InviteAdminModal, InvitationsTable } from './admin-management';
import {
  useInvitationsQuery,
  useInviteAdminMutation,
  useResendInvitation,
  useRevokeInvitation,
} from '../hooks/useAdminInvitations';
import type { InviteAdminPayload } from '../types/adminInvitation';

const PERMISSION_MODULES: { key: string; title: string; subtitle: string }[] = [
  { key: 'merchants', title: 'Merchants', subtitle: 'Manage vendors' },
  { key: 'vouchers', title: 'Vouchers', subtitle: 'Promo codes' },
  { key: 'menu_items', title: 'Menu Items', subtitle: 'Products & Pricing' },
  { key: 'user_mgmt', title: 'User Mgmt', subtitle: 'Admin accounts' },
  { key: 'reports', title: 'Reports', subtitle: 'Analytics' },
  { key: 'settings', title: 'Settings', subtitle: 'System config' },
];

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const GAP_6 = 24;

interface AdminRecord {
  id: number;
  fullName: string;
  email: string;
  role: string;
  permissions: string[];
  status: string;
  lastLoginAt: string | null;
  inviteSentAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AdminsResponse {
  success: boolean;
  data: AdminRecord[];
  meta: { total: number; page: number; limit: number };
  stats: {
    totalAdmins: number;
    superAdmins: number;
    activeNow: number;
    pendingInvites: number;
  };
}

function formatLastLogin(value: string | null): string {
  if (value == null || value === '') return '–';
  const m = vnDayjs(value);
  const now = vnDayjs();
  const days = now.diff(m, 'days');
  if (days >= 365) return m.format('MMM DD, YYYY');
  if (days >= 1) return m.format('MMM DD, YYYY');
  const hours = now.diff(m, 'hours');
  if (hours >= 1) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const mins = now.diff(m, 'minutes');
  if (mins >= 1) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
  return 'Just now';
}

function permissionsDisplay(permissions: string[]): string {
  if (!Array.isArray(permissions) || permissions.length === 0) return '–';
  return permissions.join(', ');
}

export default function AdminManagement() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState<AdminRecord[]>([]);
  const [stats, setStats] = useState<AdminsResponse['stats']>({
    totalAdmins: 0,
    superAdmins: 0,
    activeNow: 0,
    pendingInvites: 0,
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [invSearchInput, setInvSearchInput] = useState('');
  const [invSearch, setInvSearch] = useState('');
  const [invPage, setInvPage] = useState(1);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  // Edit Role modal state
  const [editRoleRecord, setEditRoleRecord] = useState<AdminRecord | null>(
    null
  );
  const [editRoleValue, setEditRoleValue] = useState<string>('');
  const [editRoleSaving, setEditRoleSaving] = useState(false);

  // Edit Permissions modal state
  const [editPermsRecord, setEditPermsRecord] = useState<AdminRecord | null>(
    null
  );
  const [permsFormState, setPermsFormState] = useState<PermissionsMap>({});
  const [editPermsSaving, setEditPermsSaving] = useState(false);

  const openPermissionsModal = (record: AdminRecord) => {
    const perms: PermissionsMap = {};
    PERMISSION_MODULES.forEach(mod => {
      const existing =
        record.permissions &&
        typeof record.permissions === 'object' &&
        !Array.isArray(record.permissions)
          ? (record.permissions as Record<string, string>)[mod.key]
          : undefined;
      perms[mod.key] = (existing as PermissionLevel) || 'none';
    });
    setPermsFormState(perms);
    setEditPermsRecord(record);
  };

  const handlePermissionsSave = async () => {
    if (!editPermsRecord) return;
    try {
      setEditPermsSaving(true);
      const res = await api.patch(
        `/admin/admins/${editPermsRecord.id}/permissions`,
        { permissions: permsFormState }
      );
      if (res.data?.success && res.data?.data) {
        setData(prev =>
          prev.map(r =>
            r.id === editPermsRecord.id
              ? { ...r, permissions: res.data.data.permissions }
              : r
          )
        );
        message.success('Permissions updated');
        setEditPermsRecord(null);
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(
        ax.response?.data?.message || 'Failed to update permissions'
      );
    } finally {
      setEditPermsSaving(false);
    }
  };

  const inviteMutation = useInviteAdminMutation();
  const resendMutation = useResendInvitation();
  const revokeMutation = useRevokeInvitation();

  const {
    data: invitationsData,
    isLoading: invitationsLoading,
    refetch: refetchInvitations,
  } = useInvitationsQuery({
    page: invPage,
    limit: 10,
    q: invSearch,
  });

  const invitations = invitationsData?.items ?? [];
  const invitationsTotal = invitationsData?.total ?? 0;

  const handleInviteSubmit = async (payload: InviteAdminPayload) => {
    await inviteMutation.mutateAsync(payload);
    setInviteModalOpen(false);
    refetchInvitations();
    fetchAdmins();
  };

  const handleResend = async (record: { id: string }) => {
    setResendingId(record.id);
    try {
      await resendMutation.mutateAsync(record.id);
      refetchInvitations();
      fetchAdmins();
    } finally {
      setResendingId(null);
    }
  };

  const handleRevoke = async (record: { id: string }) => {
    Modal.confirm({
      title: 'Hủy lời mời?',
      content:
        'Lời mời này sẽ bị hủy vĩnh viễn. Người được mời sẽ không thể chấp nhận.',
      okText: 'Hủy lời mời',
      okType: 'danger',
      cancelText: 'Bỏ qua',
      onOk: async () => {
        setRevokingId(record.id);
        try {
          await revokeMutation.mutateAsync(record.id);
          refetchInvitations();
          fetchAdmins();
        } finally {
          setRevokingId(null);
        }
      },
    });
  };

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = {
        page,
        limit,
      };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await api.get<AdminsResponse>('/admin/admins', { params });
      const body = res.data;
      if (body.success && body.data) {
        setData(body.data);
        setTotal(body.meta?.total ?? 0);
        if (body.stats) setStats(body.stats);
      }
    } catch (err: unknown) {
      const ax = err as {
        response?: { data?: { message?: string }; status?: number };
      };
      message.error(ax.response?.data?.message || 'Failed to load admins');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, roleFilter, statusFilter]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };
  const handleInviteClick = () => {
    setInviteModalOpen(true);
  };

  const handleEditRoleSave = async () => {
    if (!editRoleRecord) return;
    try {
      setEditRoleSaving(true);
      await api.patch(`/admin/admins/${editRoleRecord.id}/role`, {
        role: editRoleValue,
      });
      message.success('Role updated');
      setData(prev =>
        prev.map(r =>
          r.id === editRoleRecord.id ? { ...r, role: editRoleValue } : r
        )
      );
      setEditRoleRecord(null);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to update role');
    } finally {
      setEditRoleSaving(false);
    }
  };

  const handleActivateToggle = async (record: AdminRecord) => {
    const newStatus = record.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    try {
      await api.patch(`/admin/admins/${record.id}/status`, {
        status: newStatus,
      });
      message.success(
        `Admin ${newStatus === 'ACTIVE' ? 'activated' : 'deactivated'}`
      );
      setData(prev =>
        prev.map(r => (r.id === record.id ? { ...r, status: newStatus } : r))
      );
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to update status');
    }
  };

  const handleRemoveAdmin = (record: AdminRecord) => {
    Modal.confirm({
      title: 'Remove Admin',
      content: `Are you sure you want to remove ${
        record.fullName || record.email
      }? This cannot be undone.`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.delete(`/admin/admins/${record.id}`);
          message.success('Admin removed');
          fetchAdmins();
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string } } };
          message.error(ax.response?.data?.message || 'Failed to remove admin');
        }
      },
    });
  };

  const actor = currentUser
    ? { id: currentUser.id, role: currentUser.role }
    : null;

  const getActionItems = (record: AdminRecord): MenuProps['items'] => {
    const canEditRole =
      !!actor &&
      canUpdatePermissions(actor, { id: record.id, role: record.role });
    const canToggleStatus =
      !!actor &&
      canDeactivateAdmin(actor, { id: record.id, role: record.role });

    return [
      {
        key: 'edit-role',
        label: 'Edit Role',
        disabled: !canEditRole,
        onClick: () => {
          setEditRoleValue(record.role);
          setEditRoleRecord(record);
        },
      },
      {
        key: 'edit-permissions',
        label: 'Edit Permissions',
        disabled: !canEditRole,
        onClick: () => openPermissionsModal(record),
      },
      {
        key: 'activate',
        label: record.status === 'ACTIVE' ? 'Deactivate' : 'Activate',
        disabled: !canToggleStatus,
        onClick: () => handleActivateToggle(record),
      },
      {
        key: 'resend',
        label: 'Resend Invite',
        disabled: record.status !== 'PENDING',
        onClick: () => message.info('Use the Invitations tab to resend'),
      },
      {
        key: 'remove',
        label: 'Remove',
        danger: true,
        disabled: !canToggleStatus,
        onClick: () => handleRemoveAdmin(record),
      },
    ];
  };

  const roleLabel = (r: string) => {
    if (r === 'super_admin') return 'Super Admin';
    if (r === 'editor') return 'Editor';
    if (r === 'viewer') return 'Viewer';
    return r;
  };

  const roleTag = (r: string) => {
    const label = roleLabel(r);
    if (r === 'super_admin') return <Tag color="purple">{label}</Tag>;
    if (r === 'editor') return <Tag color="blue">{label}</Tag>;
    return <Tag color="default">{label}</Tag>;
  };

  const statusDot = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'ACTIVE') return { color: '#22c55e', label: 'Active' };
    if (s === 'PENDING') return { color: '#f97316', label: 'Pending' };
    return { color: '#9ca3af', label: 'Inactive' };
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);

  return (
    <div
      style={{
        padding: GAP_6,
        maxWidth: 1280,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: GAP_6,
        background: 'transparent',
      }}
    >
      {/* Summary cards – 4 equal columns */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: GAP_6,
        }}
      >
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
                Total Admins
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                {stats.totalAdmins}
              </div>
            </div>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#dbeafe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TeamOutlined style={{ fontSize: 22, color: '#2563eb' }} />
            </div>
          </div>
        </Card>
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
                Super Admins
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                {stats.superAdmins}
              </div>
            </div>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#ede9fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SafetyCertificateOutlined
                style={{ fontSize: 22, color: '#7c3aed' }}
              />
            </div>
          </div>
        </Card>
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
                Active Now
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                {stats.activeNow}
              </div>
            </div>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: '#22c55e',
                }}
              />
            </div>
          </div>
        </Card>
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
            }}
          >
            <div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>
                Pending Invites
              </div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}>
                {stats.pendingInvites}
              </div>
            </div>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#fef9c3',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MailOutlined style={{ fontSize: 22, color: '#eab308' }} />
            </div>
          </div>
        </Card>
      </div>

      <Tabs
        defaultActiveKey="admins"
        items={[
          {
            key: 'admins',
            label: 'Admins',
            children: (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: GAP_6 }}
              >
                {/* Filter bar */}
                <Card
                  style={ROUNDED_CARD_STYLE}
                  bodyStyle={{ padding: 16 }}
                  className="admin-mgmt-filter-bar"
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 12,
                    }}
                  >
                    <div
                      style={{ flex: '1 1 40%', minWidth: 200, maxWidth: 400 }}
                    >
                      <Input
                        placeholder="Search by name or email..."
                        prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                        onPressEnter={handleSearch}
                        allowClear
                        style={{ borderRadius: 8 }}
                      />
                    </div>
                    <Select
                      placeholder="All Roles"
                      value={roleFilter ?? undefined}
                      onChange={v => {
                        setRoleFilter(v || undefined);
                        setPage(1);
                      }}
                      style={{ width: 140, borderRadius: 8 }}
                      allowClear
                      options={[
                        { value: 'super_admin', label: 'Super Admin' },
                        { value: 'editor', label: 'Editor' },
                        { value: 'viewer', label: 'Viewer' },
                      ]}
                    />
                    <Select
                      placeholder="All Status"
                      value={statusFilter ?? undefined}
                      onChange={v => {
                        setStatusFilter(v || undefined);
                        setPage(1);
                      }}
                      style={{ width: 140, borderRadius: 8 }}
                      allowClear
                      options={[
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'INACTIVE', label: 'Inactive' },
                        { value: 'PENDING', label: 'Pending' },
                      ]}
                    />
                    <div style={{ flex: '1 1 auto', minWidth: 0 }} />
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleInviteClick}
                    >
                      Invite New Admin
                    </Button>
                  </div>
                </Card>

                {/* Table */}
                <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 0 }}>
                  <Table<AdminRecord>
                    loading={loading}
                    dataSource={data}
                    rowKey="id"
                    pagination={false}
                    size="middle"
                    style={{ borderRadius: 16, overflow: 'hidden' }}
                    columns={[
                      {
                        title: 'ADMIN USER',
                        dataIndex: 'fullName',
                        key: 'adminUser',
                        render: (_, record) => (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 12,
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              navigate(`/admin-management/${record.id}`, {
                                state: { admin: record },
                              })
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/admin-management/${record.id}`, {
                                  state: { admin: record },
                                });
                              }
                            }}
                          >
                            <Avatar
                              size={40}
                              icon={<UserOutlined />}
                              style={{
                                backgroundColor:
                                  record.status === 'PENDING'
                                    ? '#fef9c3'
                                    : '#e5e7eb',
                                color:
                                  record.status === 'PENDING'
                                    ? '#eab308'
                                    : '#6b7280',
                              }}
                            />
                            <div>
                              <div
                                style={{ fontWeight: 600, color: '#111827' }}
                              >
                                {record.status === 'PENDING' && !record.fullName
                                  ? 'Invite Sent'
                                  : record.fullName || '–'}
                              </div>
                              <div style={{ fontSize: 13, color: '#6b7280' }}>
                                {record.email || '–'}
                              </div>
                            </div>
                          </div>
                        ),
                      },
                      {
                        title: 'ROLE',
                        dataIndex: 'role',
                        key: 'role',
                        width: 140,
                        render: (role: string) => roleTag(role),
                      },
                      {
                        title: 'PERMISSIONS',
                        dataIndex: 'permissions',
                        key: 'permissions',
                        render: (p: string[]) => (
                          <span style={{ color: '#374151' }}>
                            {permissionsDisplay(p)}
                          </span>
                        ),
                      },
                      {
                        title: 'STATUS',
                        dataIndex: 'status',
                        key: 'status',
                        width: 120,
                        render: (status: string) => {
                          const { color, label } = statusDot(status);
                          return (
                            <Space size={6}>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  background: color,
                                  display: 'inline-block',
                                }}
                              />
                              <span style={{ color }}>{label}</span>
                            </Space>
                          );
                        },
                      },
                      {
                        title: 'LAST LOGIN',
                        dataIndex: 'lastLoginAt',
                        key: 'lastLoginAt',
                        width: 140,
                        render: (v: string | null) => (
                          <span style={{ color: '#374151' }}>
                            {formatLastLogin(v)}
                          </span>
                        ),
                      },
                      {
                        title: 'ACTIONS',
                        key: 'actions',
                        width: 80,
                        align: 'right',
                        render: (_, record) => (
                          <Dropdown
                            menu={{ items: getActionItems(record) }}
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button
                              type="text"
                              icon={<EllipsisOutlined />}
                              style={{ color: '#6b7280' }}
                              aria-label="Actions"
                            />
                          </Dropdown>
                        ),
                      },
                    ]}
                  />
                  {/* Pagination bar */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderTop: '1px solid #f0f0f0',
                    }}
                  >
                    <span style={{ fontSize: 14, color: '#6b7280' }}>
                      Showing {total === 0 ? 0 : start} to {end} of {total}{' '}
                      results
                    </span>
                    <Space>
                      <Button
                        disabled={page <= 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        disabled={page * limit >= total}
                        onClick={() => setPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </Space>
                  </div>
                </Card>
              </div>
            ),
          },
          {
            key: 'invitations',
            label: 'Invitations',
            children: (
              <InvitationsTable
                data={invitations}
                loading={invitationsLoading}
                total={invitationsTotal}
                page={invPage}
                limit={10}
                search={invSearchInput}
                onSearchChange={setInvSearchInput}
                onPageChange={setInvPage}
                onSearch={() => {
                  setInvSearch(invSearchInput.trim());
                  setInvPage(1);
                }}
                onResend={handleResend}
                onRevoke={handleRevoke}
                onInviteClick={handleInviteClick}
                resendingId={resendingId}
                revokingId={revokingId}
              />
            ),
          },
        ]}
      />

      <InviteAdminModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSubmit={handleInviteSubmit}
        loading={inviteMutation.isPending}
      />

      {/* Edit Role Modal */}
      <Modal
        title={`Edit Role — ${
          editRoleRecord?.fullName || editRoleRecord?.email || ''
        }`}
        open={!!editRoleRecord}
        onCancel={() => setEditRoleRecord(null)}
        onOk={handleEditRoleSave}
        confirmLoading={editRoleSaving}
        okText="Save"
        destroyOnClose
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="Role">
            <Select
              value={editRoleValue}
              onChange={setEditRoleValue}
              style={{ width: '100%' }}
              options={[
                { value: 'admin', label: 'Admin' },
                { value: 'support', label: 'Support' },
                { value: 'editor', label: 'Editor' },
                { value: 'viewer', label: 'Viewer' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Permissions Modal */}
      <Modal
        title={`Permissions — ${
          editPermsRecord?.fullName || editPermsRecord?.email || ''
        }`}
        open={!!editPermsRecord}
        onCancel={() => setEditPermsRecord(null)}
        onOk={handlePermissionsSave}
        confirmLoading={editPermsSaving}
        okText="Save changes"
        destroyOnClose
        width={520}
      >
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 16,
            }}
          >
            <Button
              size="small"
              onClick={() =>
                setPermsFormState({ ...DEFAULT_SUPPORT_PERMISSIONS })
              }
            >
              Support default
            </Button>
            <Button
              size="small"
              onClick={() =>
                setPermsFormState({ ...DEFAULT_ADMIN_PERMISSIONS })
              }
            >
              Admin default
            </Button>
            <Button
              size="small"
              onClick={() => setPermsFormState({ ...FULL_PERMISSIONS })}
            >
              Full access
            </Button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {PERMISSION_MODULES.map(mod => (
              <div
                key={mod.key}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{mod.title}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    {mod.subtitle}
                  </div>
                </div>
                <Select
                  value={(permsFormState[mod.key] as PermissionLevel) || 'none'}
                  onChange={(v: PermissionLevel) =>
                    setPermsFormState(p => ({ ...p, [mod.key]: v }))
                  }
                  style={{ width: 120 }}
                  options={[
                    { value: 'none', label: 'None' },
                    { value: 'read', label: 'Read' },
                    { value: 'write', label: 'Write' },
                  ]}
                />
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
}
